import React, { Fragment, useId } from "react";
import overpayLogo from "components/Icon/icons/overpay.svg";
import tetherLogo from "components/UsdtDepositForm/assets/TetherUSDTLogo.png";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import {
  getPaymentOptionLabel,
  USDT_PAYMENT_OPTION,
  type PaymentOption,
} from "utils/paymentMethods";
import payPalLogo from "./assets/PayPal.svg";
import {
  PaymentMethodInput,
  PaymentMethodLabel,
  PaymentMethodLogo,
  PaymentMethodRadio,
  PaymentMethodTitle,
  PaymentOptions,
} from "./PaymentMethodSelector.styles";

interface PaymentMethodSelectorProps {
  paymentMethods: PaymentOption[];
  selectedPaymentMethod?: PaymentOption;
  onSelect: (method: PaymentOption) => void;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = React.memo(
  ({ paymentMethods, selectedPaymentMethod, onSelect }) => {
    const { t } = useTranslation("common");
    const groupName = useId();

    return (
      <PaymentOptions role="radiogroup" aria-label={t("parcels.paymentMethod")}>
        {paymentMethods.map((method) => {
          const id = `${groupName}-${method}`;
          const isSelected = selectedPaymentMethod === method;
          const label = getPaymentOptionLabel(t, method);

          // No `default`: a new PaymentOption member must get a case here.
          let methodPresentation: React.ReactNode;
          switch (method) {
            case "paypal":
              methodPresentation = (
                <PaymentMethodLogo src={payPalLogo} alt={label} />
              );
              break;
            case "overpay":
              methodPresentation = (
                <PaymentMethodLogo src={overpayLogo} alt={label} />
              );
              break;
            case USDT_PAYMENT_OPTION:
              methodPresentation = (
                <>
                  <PaymentMethodLogo src={tetherLogo} alt="" />
                  <PaymentMethodTitle>{label}</PaymentMethodTitle>
                </>
              );
              break;
            case "credit_card":
            case "stripe":
              methodPresentation = (
                <PaymentMethodTitle>{label}</PaymentMethodTitle>
              );
              break;
          }

          // A native radio, so Tab reaches the group once and the arrow keys
          // move between methods; the label draws it.
          return (
            <Fragment key={method}>
              <PaymentMethodInput
                type="radio"
                id={id}
                name={groupName}
                value={method}
                checked={isSelected}
                onChange={() => onSelect(method)}
              />
              <PaymentMethodLabel htmlFor={id}>
                <PaymentMethodRadio
                  type={
                    isSelected
                      ? IconType.RadioButtonFill
                      : IconType.RadioButtonLight
                  }
                  $selected={isSelected}
                />
                {methodPresentation}
              </PaymentMethodLabel>
            </Fragment>
          );
        })}
      </PaymentOptions>
    );
  },
);

export default PaymentMethodSelector;
